import {
  type DocumentUpdateAttempt,
  openDocumentUpdateAttempt,
} from '@/lib/telemetry/documentUpdateTracking';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { sanitizeError } from '@heidi/runtime/logger';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { ContentType } from '@heidi/schema/ml-scribe';
import { useMutateUpdateSyncDocumentById as useMutateUpdateSyncDocumentByIdCore } from '@heidi/session/data';
import { useSessionEhrProvider } from '../integrations/useSessionEhrProvider';
import { useEnableBlockEditorV1 } from '../useEnableBlockEditorV1';

/**
 * Persists document edits to the server.
 *
 * @param options.contentTypeOverride - Bypass the block-editor `content_type`
 *   and label the saved document with the caller-supplied type.
 */
export function useMutateUpdateSyncDocumentById(options?: {
  contentTypeOverride?: ContentType;
}) {
  const enableBlockEditorV1 = useEnableBlockEditorV1(
    useSessionEhrProvider(useCurrentSessionId()).sessionEhrProvider
  );
  const contentType =
    options?.contentTypeOverride ??
    (enableBlockEditorV1 ? ContentType.HTML : ContentType.MARKDOWN);

  return useMutateUpdateSyncDocumentByIdCore<DocumentUpdateAttempt>({
    contentType,
    openAttempt: openDocumentUpdateAttempt,
    onUpdateError: (error, { sessionId, documentId }) => {
      logger.error('Failed to update document', {
        session_id: sessionId,
        document_id: documentId,
        error: sanitizeError(error),
      });
      toast({
        tone: 'destructive',
        title: 'Failed to update document',
        description: error.message,
      });
    },
  });
}
